import { useState, useEffect } from 'react';
import { Bars } from 'react-loader-spinner';
import { useAddons } from '../../../../../hooks/useAddons';
import { ToggleWithLabel } from '../ToggleWithLabel';
import { MainCustomButton } from '../../../../../components/Buttons';
import { useTranslation } from 'react-i18next';
import { JurisdictionSelect } from '../JurisdictionSelect';
import { errorToast, successToast } from '../../../../../utils/toasts';
import { GetAddonsResponse, UpdateAddonsPayload } from '../../../../../services/Interfaces/IAccountService';

type EditableAddon = {
  id: number;
  description: string;
  value: string;
  active: boolean;
  jurisdiction_id: number;
};

export const AddonsTab = () => {
  const { addons, loading, updateAddons } = useAddons();
  const [editableAddons, setEditableAddons] = useState<EditableAddon[]>([]);
  const [selectedJurisdictionId, setSelectedJurisdictionId] = useState<number | null>(null);
  const [originalAddons, setOriginalAddons] = useState<EditableAddon[]>([]);

  const { t } = useTranslation();

  useEffect(() => {
    if (addons.length > 0 && selectedJurisdictionId !== null) {
      const filtered = addons
        .filter(a => a.addon_type != 'transfer_company')
        .map(a => ({
          id: a.addon_id,
          description: a.addon_description,
          value: String(a.value),
          active: a.is_active === 1,
          jurisdiction_id: a.jurisdiction_id,
        }));
      
      setEditableAddons(filtered);
      setOriginalAddons(filtered);
    }
  }, [addons, selectedJurisdictionId]);
  

  const toggleAddon = (id: number) => {
    setEditableAddons(prev =>
      prev.map(addon =>
        addon.id === id ? { ...addon, active: !addon.active } : addon
      )
    );
  };

  const updateAddonValue = (id: number, newValue: string) => {
    setEditableAddons(prev =>
      prev.map(addon =>
        addon.id === id ? { ...addon, value: newValue } : addon
      )
    );
  };

  const handleSave = async () => {
    try {
      const changedAddons = editableAddons.filter((addon, index) => {
        const original = originalAddons[index];
        return (
          addon.value !== original.value ||
          addon.active !== original.active
        );
      });
  
      if (changedAddons.length === 0) {
        successToast(t('environment.noChangesToSave'));
        return;
      }

      const payload: UpdateAddonsPayload[] = changedAddons.map(addon => ({
        addon_id: addon.id,
        value: Number(addon.value),
        is_active: addon.active
      }));
      
      await updateAddons(payload);
      
  
    } catch (error) {
      errorToast(t('environment.errorSavingAddons'));
    }
  };
  

  if (loading)
    return (
      <div className="flex items-center justify-center py-6">
        <Bars height="32" width="32" color="#4B5563" />
      </div>
    );

  return (
    <div className="flex flex-col gap-4">
      <JurisdictionSelect
        selectedJurisdictionId={selectedJurisdictionId}
        setSelectedJurisdictionId={setSelectedJurisdictionId}
      />

      <h2 className="text-lg font-medium mt-4">{t('environment.manageAddons')}</h2>

      {editableAddons.map(addon => (
        <div
          key={addon.id}
          className="border border-dashed p-4 rounded flex items-center justify-between"
        >
          <div className="flex flex-col">
            <label className="text-sm">{addon.description}:</label>
            {/* <input
              type="text"
              value={addon.value}
              onChange={(e) => updateAddonValue(addon.id, e.target.value)}
              className="mt-1 border rounded px-3 py-1 w-40"
            /> */}

            <div className="relative mt-1 w-40">
              <span className="absolute left-3 inset-y-0 flex items-center pointer-events-none">$</span>
              <input
                type="text"
                value={addon.value}
                onChange={(e) => updateAddonValue(addon.id, e.target.value)}
                className="pl-7 pr-2 py-1.5 border rounded w-full"
              />
            </div>
          </div>
          <ToggleWithLabel
            checked={addon.active}
            onChange={() => toggleAddon(addon.id)}
          />
        </div>
      ))}

      <MainCustomButton text={t('others.othSave')} onClick={handleSave} />
    </div>
  );
};
